import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Loads the badge PNG with the auth header and returns an object URL.
export const useBadgeImage = (badgeId, token) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!badgeId || !token) return undefined;
    let url;
    axios.get(`${API}/lab-badges/${badgeId}/image`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(null));
    return () => url && URL.revokeObjectURL(url);
  }, [badgeId, token]);
  return src;
};

export const downloadBadge = (src, title) => {
  if (!src) return toast.error('Badge is still loading');
  const a = document.createElement('a');
  a.href = src;
  a.download = `Navigate_Badge_${(title || 'Lab').replace(/[^a-z0-9]+/gi, '_')}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  return null;
};

export function LabBadgeImage({ badge, token, className = 'w-40 h-40' }) {
  const src = useBadgeImage(badge?.badge_id, token);
  if (!src) return <div className={`${className} flex items-center justify-center`}><Loader2 className="h-6 w-6 animate-spin text-navigate-gold" /></div>;
  return <img src={src} alt={`Navigate badge: ${badge.lab_title}`} className={`${className} object-contain drop-shadow-[0_10px_30px_rgba(212,175,55,0.25)]`} data-testid={`lab-badge-img-${badge.badge_id}`} />;
}

export function LabBadgeEarned({ badge, token }) {
  const src = useBadgeImage(badge.badge_id, token);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-6 rounded-2xl border border-navigate-gold/40 bg-navigate-gold/5 p-6 mb-6" data-testid="lab-badge-earned">
      {src ? <img src={src} alt="Navigate badge" className="w-44 h-44 object-contain drop-shadow-[0_10px_30px_rgba(212,175,55,0.3)] animate-in zoom-in-75 duration-500" /> : <Loader2 className="h-8 w-8 animate-spin text-navigate-gold m-16" />}
      <div className="text-center sm:text-left">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-navigate-gold">{badge.newly_awarded === false ? 'Badge already earned' : 'Badge earned'}</p>
        <h4 className="text-xl font-semibold text-white mt-1">{badge.lab_title}</h4>
        <p className="text-sm text-zinc-400 mt-1">Your Navigate Learning Lab badge is saved to My Badges on your dashboard.</p>
        <button type="button" onClick={() => downloadBadge(src, badge.lab_title)} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navigate-gold text-black text-sm font-medium hover:bg-navigate-gold-light transition-colors" data-testid="lab-badge-download-btn">
          <Download className="h-4 w-4" />Download badge
        </button>
      </div>
    </div>
  );
}
